// SYS-09 — who may send events to Sentry, and how often a realtime drop is sent.
//
// The free plan accepts 5,000 error events a month. In September it was used up
// in six days (8–13.09), almost all of it by realtime channel_error / timed_out
// warnings (8 tables per burst) and by test browsers on localhost, and Sentry
// then received nothing until the quota reset. This module is the rule that
// keeps the quota for real users on the real site.
//
// SYS-11 — after SYS-09 a realtime drop still went out once per table per PAGE
// LOAD, so a working day (~250 events, all 7 tables dropping together behind
// the China network) would use the quota up around 4.11, before the 8.11
// reset. firstTimeToday() now lets each drop through once per BROWSER per
// local day; every repeat stays a breadcrumb.
//
// Pure on purpose: no React, no Sentry import, no location / navigator.
// The caller (src/index.js) passes the three facts in, so a Node test can load
// this file with nothing but Babel. The one browser read is browserDay(), which
// touches window.localStorage defensively and is never called by the pure
// functions; in Node there is no window, so it answers storage: null.

// Hosts that are never the real site. `[::1]` is how a browser reports the IPv6
// loopback in `location.hostname`; `::1` is the bare form.
const BLOCKED_HOSTS = new Set(['localhost', '127.0.0.1', '::1', '[::1]', '0.0.0.0']);

// true only for a production build, on a real host, in a non-headless browser.
// Any missing input answers false: the gate fails closed.
export function shouldReport({ nodeEnv, hostname, userAgent } = {}) {
  if (!nodeEnv || !hostname || !userAgent) return false;
  if (nodeEnv !== 'production') return false;
  const host = String(hostname).toLowerCase();
  if (BLOCKED_HOSTS.has(host)) return false;
  if (host.endsWith('.local')) return false;
  if (String(userAgent).includes('HeadlessChrome')) return false;
  return true;
}

// Keys already reported in this page session. Module-level, so it lives as
// long as the page: a reload starts a new session and may report once again.
const seen = new Set();

// true the first time a key is seen in this page session, false after that.
export function firstTimeThisSession(key) {
  if (seen.has(key)) return false;
  seen.add(key);
  return true;
}

// The browser's local calendar day, e.g. "2026-10-10".
export function localDayKey(date) {
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}

// One localStorage entry for every key sent today: { day, keys: [...] }.
// Shared by every tab of the browser, so a second tab does not send again.
export const DAY_STORAGE_KEY = 'clb.sentry.sentToday';

function readSentToday(storage, today) {
  const raw = storage.getItem(DAY_STORAGE_KEY);
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.day !== today || !Array.isArray(parsed.keys)) return [];
    return parsed.keys.filter((k) => typeof k === 'string');
  } catch {
    return []; // a corrupt entry counts as nothing sent today
  }
}

// true the first time a key is seen today in this browser, false after that.
// Within one page it never answers true twice. When storage is missing or
// throws (a private window, blocked site data) it falls back to once per page
// session — the SYS-09 rule — rather than sending every time.
export function firstTimeToday(key, { storage, today } = {}) {
  if (!firstTimeThisSession(key)) return false;
  if (!storage || !today) return true;
  try {
    const keys = readSentToday(storage, today);
    if (keys.includes(key)) return false;
    storage.setItem(DAY_STORAGE_KEY, JSON.stringify({ day: today, keys: [...keys, key] }));
    return true;
  } catch {
    return true;
  }
}

// The browser facts firstTimeToday() needs. Reading localStorage can throw
// (SecurityError when site data is blocked), so it is read once, guarded.
export function browserDay(now = new Date()) {
  let storage = null;
  try {
    storage = typeof window !== 'undefined' && window.localStorage ? window.localStorage : null;
  } catch {
    storage = null;
  }
  return { storage, today: localDayKey(now) };
}

// Tests only: forget every key, as a page reload would.
export function __resetForTests() {
  seen.clear();
}
